export interface PricingPlan {
  name: string;
  price: string;
  billing: string;
  popular?: boolean;
  popularBadge?: string;
  budgetNote?: string;
  targetAudience?: string;
  features: string[];
  cta: string;
}

export interface CategoryFaq {
  question: string;
  answer: string;
}

export interface PricingCategory {
  id: string;
  label: string;
  icon: string;
  note?: string;
  noteStyle?: 'subtitle' | 'info';
  faqs?: CategoryFaq[];
  plans: PricingPlan[];
}

export const pricingCategories: PricingCategory[] = [
  {
    id: 'weby',
    label: 'Tvorba webov',
    icon: 'ri-global-line',
    note: 'Ceny nezahŕňajú doménu ani hosting. Doménu a hosting vám vieme odporučiť a pomôcť s nastavením.',
    plans: [
      {
        name: 'Štartovací web',
        price: '490 €',
        billing: 'jednorazovo',
        targetAudience: 'Ideálny štart na webe',
        features: [
          'Moderný responzívny web do 5 strán',
          'Plná mobilná optimalizácia',
          'Základné SEO nastavenie',
          'Kontaktný formulár',
          'Google Analytics integrácia',
          'Dodanie do 7 dní',
        ],
        cta: 'Objednať',
      },
      {
        name: 'Profesionálny web',
        price: '890 €',
        billing: 'jednorazovo',
        popular: true,
        popularBadge: '⭐ Najpopulárnejší',
        targetAudience: 'Najobľúbenejšia voľba',
        features: [
          'Web do 8 strán s prémiovým dizajnom',
          'Pokročilé animácie a efekty',
          'Viacjazyčnosť SK/EN',
          'Kontaktné formuláre na mieru',
          '30 dní podpora zadarmo',
          'Priority SEO optimalizácia',
        ],
        cta: 'Objednať',
      },
      {
        name: 'Firemný web',
        price: 'od 1 490 €',
        billing: 'jednorazovo',
        targetAudience: 'Na mieru bez kompromisov',
        features: [
          'Plne custom riešenie na mieru',
          'Neobmedzený počet strán',
          'Pokročilé integrácie a API',
          'Prioritná podpora 90 dní',
          'Priamy kontakt počas celého projektu',
          'Kompletná dokumentácia',
        ],
        cta: 'Konzultácia',
      },
    ],
  },
  {
    id: 'eshopy',
    label: 'Tvorba e-shopov',
    icon: 'ri-shopping-cart-line',
    note: 'Ceny nezahŕňajú doménu ani hosting. Doménu a hosting vám vieme odporučiť a pomôcť s nastavením.',
    plans: [
      {
        name: 'Mini e-shop',
        price: '790 €',
        billing: 'jednorazovo',
        targetAudience: 'Ideálny štart na webe',
        features: [
          'Do 20 produktov',
          'WooCommerce platforma',
          'Platobná brána Stripe / PayPal',
          'Základný dizajn e-shopu',
          'Email notifikácie zákazníkom',
          'Mobilná verzia',
        ],
        cta: 'Objednať',
      },
      {
        name: 'Štandardný e-shop',
        price: '1 490 €',
        billing: 'jednorazovo',
        popular: true,
        popularBadge: '⭐ Najpopulárnejší',
        targetAudience: 'Najobľúbenejšia voľba',
        features: [
          'Do 100 produktov',
          'Vlastný prémiový dizajn',
          'Pokročilý admin panel',
          'Skladové hospodárstvo',
          'Napojenie na kuriérov',
          '30 dní podpora zadarmo',
        ],
        cta: 'Objednať',
      },
      {
        name: 'Custom e-shop',
        price: 'od 2 490 €',
        billing: 'jednorazovo',
        targetAudience: 'Na mieru bez kompromisov',
        features: [
          'Vlastné funkcie a logika',
          'Rezervačný systém / košík',
          'CRM napojenie',
          'API integrácie na mieru',
          'Prioritná podpora',
          'Škálovateľná architektúra',
        ],
        cta: 'Konzultácia',
      },
    ],
  },
  {
    id: 'marketing',
    label: 'Správa marketingu',
    icon: 'ri-bar-chart-grouped-line',
    plans: [
      {
        name: 'Štart marketing',
        price: '199 €',
        billing: 'mesačne',
        budgetNote: 'Odporúčaný reklamný rozpočet: min. 150 €/mes',
        targetAudience: 'Ideálny štart na webe',
        features: [
          '1 platforma (Google Ads alebo Meta Ads)',
          'Kompletný setup kampaní',
          'Správa a optimalizácia',
          'Mesačný výkonnostný report',
          'Cielenie na správne publikum',
          'Základné A/B testovanie',
        ],
        cta: 'Začať',
      },
      {
        name: 'Rast marketing',
        price: '349 €',
        billing: 'mesačne',
        popular: true,
        popularBadge: '🎯 Odporúčame',
        budgetNote: 'Odporúčaný reklamný rozpočet: min. 300 €/mes',
        targetAudience: 'Najobľúbenejšia voľba',
        features: [
          'Google Ads + Meta Ads',
          'Pokročilé A/B testovanie',
          'Retargeting kampane',
          'Pokročilá analytika a sledovanie',
          'Týždenný report',
          'Optimalizácia konverzného lievika',
        ],
        cta: 'Začať',
      },
      {
        name: 'Pro marketing',
        price: '549 €',
        billing: 'mesačne',
        budgetNote: 'Odporúčaný reklamný rozpočet: min. 500 €/mes',
        targetAudience: 'Na mieru bez kompromisov',
        features: [
          'Všetky reklamné platformy',
          'Kompletná digitálna stratégia',
          'Priama komunikácia a reporting od mňa',
          'Denný monitoring výkonu',
          'Prioritná komunikácia',
          'Mesačná strategická konzultácia',
        ],
        cta: 'Začať',
      },
    ],
  },
  {
    id: 'socialne',
    label: 'Sociálne siete',
    icon: 'ri-instagram-line',
    plans: [
      {
        name: 'Štart sociálne siete',
        price: '249 €',
        billing: 'mesačne',
        targetAudience: 'Ideálny štart na webe',
        features: [
          '1 sieť (Instagram / Facebook / LinkedIn)',
          '8 príspevkov mesačne',
          'Grafické spracovanie obsahu',
          'Plánovanie a publikovanie',
          'Správa komentárov',
          'Základná analytika',
        ],
        cta: 'Začať',
      },
      {
        name: 'Rast sociálne siete',
        price: '399 €',
        billing: 'mesačne',
        popular: true,
        popularBadge: '🎯 Odporúčame',
        targetAudience: 'Najobľúbenejšia voľba',
        features: [
          '2 siete',
          '12 príspevkov mesačne',
          'Stories a Reels produkcia',
          'Správa komunity',
          'Mesačný report a stratégia',
          'Hashtagová a growth stratégia',
        ],
        cta: 'Začať',
      },
      {
        name: 'Pro sociálne siete',
        price: '599 €',
        billing: 'mesačne',
        targetAudience: 'Na mieru bez kompromisov',
        features: [
          '3 siete',
          '20 príspevkov mesačne',
          'Meta Ads setup a správa',
          'Priamy kontakt s konzultantom',
          'Prioritná komunikácia',
          'Kompletná obsahová stratégia',
        ],
        cta: 'Začať',
      },
    ],
  },
  {
    id: 'sprava',
    label: 'Správa webov',
    icon: 'ri-settings-4-line',
    plans: [
      {
        name: 'Základná správa',
        price: '69 €',
        billing: 'mesačne',
        targetAudience: 'Ideálny štart na webe',
        features: [
          'Hosting webu v cene',
          'Mesačné aktualizácie systému',
          'Denné automatické zálohy',
          'Uptime monitoring 24/7',
          'SSL monitoring a obnova',
          'Základný bezpečnostný skener',
        ],
        cta: 'Začať',
      },
      {
        name: 'Štandardná správa',
        price: '109 €',
        billing: 'mesačne',
        popular: true,
        popularBadge: '💰 Najlepší pomer ceny',
        targetAudience: 'Najobľúbenejšia voľba',
        features: [
          'Hosting webu v cene',
          'Všetko zo základnej správy',
          '2h technická podpora mesačne',
          'Rýchle opravy a bugfixy',
          'Mesačný report o stave webu',
          'Optimalizácia rýchlosti',
        ],
        cta: 'Začať',
      },
      {
        name: 'Priority správa',
        price: '229 €',
        billing: 'mesačne',
        targetAudience: 'Na mieru bez kompromisov',
        features: [
          'Hosting webu v cene',
          'Všetko zo štandardnej správy',
          '5h úprav a zmien mesačne',
          'Odpoveď do 2 hodín',
          'Podpora 7 dní v týždni',
          'Priamy kontakt s autorom projektu',
        ],
        cta: 'Začať',
      },
    ],
  },
  {
    id: 'crm',
    label: 'Systémy a CRM',
    icon: 'ri-database-2-line',
    plans: [
      {
        name: 'Základný CRM',
        price: '390 €',
        billing: 'jednorazovo',
        targetAudience: 'Jednoduchý štart s CRM',
        features: [
          'Správa klientov a kontaktov',
          'Projektový prehľad a kanban',
          'Email šablóny a mailing',
          'Základné reporty a štatistiky',
          'Dodanie do 5 dní',
          'Onboarding a zaškolenie',
        ],
        cta: 'Objednať',
      },
      {
        name: 'Rozšírený systém',
        price: '890 €',
        billing: 'jednorazovo',
        popular: true,
        popularBadge: '🎯 Odporúčame',
        targetAudience: 'Najobľúbenejšia voľba',
        features: [
          'Vlastný CRM na mieru',
          'Automatické notifikácie',
          'Integrácia s vašim webom',
          'Používateľské role a práva',
          'Školenie a kompletná dokumentácia',
          'Prioritná podpora 30 dní',
        ],
        cta: 'Objednať',
      },
      {
        name: 'CRM na mieru',
        price: 'Po konzultácii',
        billing: 'jednorazovo',
        targetAudience: 'Na mieru bez kompromisov',
        features: [
          'Plne custom riešenie',
          'API integrácie na mieru',
          'Napojenie na externé systémy',
          'Dlhodobá podpora a rozvoj',
          'Osobný prístup a dlhodobá spolupráca',
          'SLA garancia dostupnosti',
        ],
        cta: 'Konzultácia',
      },
    ],
  },
  {
    id: 'automatizacie',
    label: 'Automatizácie & AI',
    icon: 'ri-robot-2-line',
    note: 'Prepájame vaše systémy, automatizujeme opakujúce sa procesy a stavíme AI asistentov pre váš biznis. n8n · Telegram boty · AI agenti · Google Ads reporty',
    noteStyle: 'subtitle',
    faqs: [
      {
        question: 'Čo je n8n a prečo ho používate?',
        answer: 'n8n je open-source nástroj na automatizáciu pracovných postupov — predstavte si ho ako "digitálneho asistenta", ktorý prepája vaše aplikácie a robí opakujúce sa úlohy za vás. Napríklad: keď príde nový zákazník z webu, automaticky ho pridá do CRM, pošle uvítací email a upozorní vás na Telegrame. Používame ho, pretože je flexibilný, bezpečný a dáta zostávajú u vás.',
      },
      {
        question: 'Potrebujem technické znalosti alebo programovanie?',
        answer: 'Vôbec nie. Celú automatizáciu nastavíme my — vy len popíšete, čo chcete zautomatizovať. Po odovzdaní dostanete jednoduchú dokumentáciu a zaškolenie, aby ste vedeli čo sa deje. Väčšina klientov ovláda výsledok bez akýchkoľvek technických znalostí.',
      },
      {
        question: 'Čo je AI agent a na čo mi pomôže?',
        answer: 'AI agent je inteligentný bot, ktorý dokáže odpovedať na otázky zákazníkov, spracovávať požiadavky alebo vykonávať úlohy — 24/7 bez prestávky. Môže fungovať na vašom webe, v Telegrame alebo WhatsApp. Napríklad: odpovedá na FAQ zákazníkov, kvalifikuje leady alebo automaticky triedi správy podľa priority.',
      },
      {
        question: 'Aké aplikácie a systémy viete prepojiť?',
        answer: 'Prakticky akékoľvek — Google Sheets, Gmail, Slack, Telegram, WhatsApp, CRM systémy (HubSpot, Pipedrive), e-shopy, fakturačné systémy, Meta Ads, Google Ads a stovky ďalších. Ak máte konkrétny systém, radi overíme kompatibilitu pred začatím projektu.',
      },
      {
        question: 'Čo sa stane, ak automatizácia prestane fungovať?',
        answer: 'Každý projekt dodávame s monitoringom a alertmi — ak niečo zlyhá, dostanete okamžité upozornenie. V rámci záruky (30–90 dní podľa balíka) opravíme akýkoľvek problém zadarmo. Po záruke ponúkame mesačnú správu za fixný poplatok.',
      },
      {
        question: 'Ako dlho trvá nastavenie automatizácie?',
        answer: 'Jednoduchá automatizácia (Štart balík) je hotová do 5–7 pracovných dní. Komplexnejšie riešenia s AI agentmi (Biznis/Pro) trvajú 2–4 týždne. Vždy začíname discovery callom, kde presne zmapujeme vaše procesy a navrhneme riešenie ešte pred začatím práce.',
      },
    ],
    plans: [
      {
        name: 'Štart',
        price: '490 €',
        billing: 'jednorazovo',
        targetAudience: 'Ideálny štart na webe',
        features: [
          '1 jednoduchá automatizácia',
          'n8n workflow setup',
          'Spojenie 2-3 aplikácií',
          'Základný Telegram bot',
          'Dokumentácia a zaškolenie',
          '30 dní podpora zadarmo',
        ],
        cta: 'Objednať',
      },
      {
        name: 'Biznis',
        price: '990 €',
        billing: 'jednorazovo',
        popular: true,
        popularBadge: '⭐ Najpopulárnejší',
        targetAudience: 'Najobľúbenejšia voľba',
        features: [
          '3 komplexné automatizácie',
          'AI agent pre zákaznícku podporu',
          'Google Ads reporty automaticky',
          'Pokročilý Telegram bot',
          'CRM napojenie a synchronizácia',
          '60 dní podpora zadarmo',
        ],
        cta: 'Objednať',
      },
      {
        name: 'Pro',
        price: 'od 1 990 €',
        billing: 'jednorazovo',
        targetAudience: 'Na mieru bez kompromisov',
        features: [
          'Neobmedzené automatizácie',
          'Vlastný AI asistent na mieru',
          'API integrácie na mieru',
          'Multi-platform workflow',
          'Prioritná podpora 90 dní',
          'Pravidelné optimalizácie',
        ],
        cta: 'Konzultácia',
      },
    ],
  },
];